/**
 * AgentDecisionLedger — the first-class, hash-chained agent decision ledger.
 *
 * Renders the PERSISTED `ledger` collection (src/lib/agent/ledger.ts): every
 * APPROVED agent action and every undo/reversal, written as an append-only,
 * SHA-256 hash-chained entry. Distinct from the activity-log-derived
 * DecisionLedger — this reads the sealed chain directly and verifies it in a
 * single pass, pinpointing the exact seq where the chain breaks.
 *
 * Honest by contract: entries are never seeded. An empty chain means nothing
 * has been approved yet, and the empty state says exactly that.
 */

import { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, ShieldAlert, ScrollText } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { db } from '../lib/db/store';
import { verifyChain, type DecisionLedgerEntry } from '../lib/agent/ledger';

type VerifyState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'ok'; count: number }
  | { status: 'broken'; brokenAtSeq: number };

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AgentDecisionLedger() {
  const [entries, setEntries] = useState<DecisionLedgerEntry[]>([]);
  const [verify, setVerify] = useState<VerifyState>({ status: 'idle' });

  useEffect(() => {
    // db.subscribe emits the current snapshot immediately + on every write.
    const unsub = db.subscribe('ledger', (rows) => {
      setEntries([...rows].sort((a, b) => a.seq - b.seq));
      // A new write invalidates any prior verification badge.
      setVerify({ status: 'idle' });
    });
    return unsub;
  }, []);

  // Newest-first for display; the chain itself is oldest → newest.
  const rows = useMemo(() => [...entries].sort((a, b) => b.seq - a.seq), [entries]);

  async function onVerify() {
    setVerify({ status: 'checking' });
    try {
      const res = await verifyChain(entries);
      setVerify(
        res.ok
          ? { status: 'ok', count: entries.length }
          : { status: 'broken', brokenAtSeq: res.brokenAtSeq },
      );
    } catch {
      setVerify({ status: 'broken', brokenAtSeq: -1 });
    }
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
        }}
      >
        <span className="t-meta">
          {entries.length} sealed {entries.length === 1 ? 'entry' : 'entries'}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {verify.status === 'ok' && (
            <span
              className="glass-pill glass-pill--green"
              role="status"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.green }}
            >
              <ShieldCheck size={13} strokeWidth={2} />
              Sealed — {verify.count} {verify.count === 1 ? 'entry' : 'entries'} verified
            </span>
          )}
          {verify.status === 'broken' && (
            <span
              className="glass-pill"
              role="status"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                color: C.orangeWarm,
                borderColor: `${C.orangeWarm}55`,
              }}
            >
              <ShieldAlert size={13} strokeWidth={2} />
              Chain broken at #{verify.brokenAtSeq}
            </span>
          )}
          <button
            type="button"
            className="glass-btn"
            onClick={onVerify}
            disabled={verify.status === 'checking' || entries.length === 0}
          >
            <ShieldCheck size={14} strokeWidth={2} />
            {verify.status === 'checking' ? 'Verifying…' : 'Verify chain'}
          </button>
        </div>
      </div>

      <GlassPanel variant="subtle" animate={false} style={{ padding: 0, overflow: 'hidden' }}>
        {rows.length === 0 ? (
          <div className="crm-empty">
            <ScrollText size={20} strokeWidth={1.75} />
            <span>No approved actions yet — the ledger starts with your first approval.</span>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: C.textMuted }}>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Actor</th>
                <th style={thStyle}>Kind</th>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Summary</th>
                <th style={thStyle}>Hash</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <td style={tdStyle}>{formatWhen(e.ts)}</td>
                  <td style={tdStyle}>{e.actor}</td>
                  <td style={tdStyle}>
                    <span
                      className="glass-pill"
                      style={{
                        color: e.kind === 'reversal' ? C.orangeWarm : C.green,
                        borderColor: `${e.kind === 'reversal' ? C.orangeWarm : C.green}55`,
                      }}
                    >
                      {e.kind}
                    </span>
                  </td>
                  <td style={tdStyle}>{e.actionType}</td>
                  <td style={tdStyle}>{e.summary}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', color: C.textMuted }}>
                    {e.hash.slice(0, 10)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </GlassPanel>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '9px 12px',
  fontWeight: 600,
  fontSize: 10.5,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  color: C.text,
  verticalAlign: 'top',
};
